import * as React from "react";
import http from "../general/HttpClient";
import { Context, GetContext, UnsubscribeContext } from "../general/ApplicationContext";

interface KeyMetadata {
    id: string;
    name: string;
    permissions: string[];
    expiresAt: string;
    revokedAt?: string;
    lastUsedAt?: string;
}
interface Permission { name: string; displayName: string; description: string; }

export default function ApiKeyManager({ scope, subjectId }: { scope: "user" | "client"; subjectId: string }) {
    const [organization, setOrganization] = React.useState(Context().currentOrganization.id);
    const [keys, setKeys] = React.useState<KeyMetadata[]>([]);
    const [permissions, setPermissions] = React.useState<Permission[]>([]);
    const [selected, setSelected] = React.useState<string[]>([]);
    const [name, setName] = React.useState("");
    const [expiry, setExpiry] = React.useState(() => new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10));
    const [token, setToken] = React.useState("");
    const [error, setError] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const base = `/api/apiKeys/${scope}/${encodeURIComponent(subjectId || "")}`;
    const config = { headers: { "X-sca-ide-org-context": organization } };
    const currentContext = React.useRef("");
    currentContext.current = `${base}:${organization}`;

    React.useEffect(() => {
        const subscription = GetContext(context => setOrganization(context.currentOrganization.id));
        return () => UnsubscribeContext(subscription);
    }, []);

    React.useEffect(() => {
        let active = true;
        setToken(""); setSelected([]); setKeys([]); setPermissions([]); setError("");
        if (subjectId && organization) {
            Promise.all([http.get(base, config), http.get(`${base}/permissions`, config)])
                .then(([list, available]) => { if (active) { setKeys(list.data); setPermissions(available.data); } })
                .catch(() => { if (active) setError("Unable to load API keys for this organization."); });
        }
        return () => { active = false; };
    }, [subjectId, organization, scope]);

    const create = async (event: React.FormEvent) => {
        const operationContext = currentContext.current;
        event.preventDefault(); setBusy(true); setError(""); setToken("");
        try {
            const response = await http.post(base, { name, permissions: selected, expiresAt: new Date(`${expiry}T23:59:59Z`).toISOString() }, config);
            if (operationContext !== currentContext.current) return;
            setToken(response.data.token);
            setKeys(previous => [response.data.key, ...previous]);
            setName(""); setSelected([]);
        } catch (failure) {
            if (operationContext === currentContext.current)
                setError(typeof failure.response?.data === "string" ? failure.response.data : "Unable to create API key.");
        } finally { setBusy(false); }
    };

    const revoke = async (id: string) => {
        const operationContext = currentContext.current;
        setBusy(true); setError("");
        try {
            await http.delete(`${base}/${id}`, config);
            if (operationContext !== currentContext.current) return;
            setKeys(previous => previous.map(key => key.id === id ? { ...key, revokedAt: new Date().toISOString() } : key));
            setToken("");
        } catch { if (operationContext === currentContext.current) setError("Unable to revoke API key."); }
        finally { setBusy(false); }
    };

    return <section className="bg-white rounded-lg border border-gray-200 p-6 my-6">
        <h2 className="text-lg font-semibold text-gray-900">API keys</h2>
        <p className="text-sm text-gray-600 my-2">Connect Power BI and other tools using a bearer key. Each key is limited to this organization and the permissions you select. Access follows the owner's current permissions.</p>
        {error && <p role="alert" className="text-red-700 my-3">{error}</p>}
        {token && <div className="bg-yellow-50 border border-yellow-200 rounded p-4 my-4">
            <p className="font-medium">Copy this key now. It will only be shown once.</p>
            <code className="block break-all my-2">{token}</code>
            <button type="button" className="text-blue-700 underline mr-4" onClick={() => navigator.clipboard.writeText(token).catch(() => setError("Copy failed. Select and copy the key manually."))}>Copy key</button>
            <button type="button" className="text-gray-700 underline" onClick={() => setToken("")}>Hide key</button>
            <p className="text-sm mt-2">Send it as Authorization: Bearer &lt;key&gt;</p>
        </div>}
        <ul className="divide-y divide-gray-200 my-4">
            {keys.map(key => <li key={key.id} className="py-3 flex justify-between gap-4">
                <div><p className="font-medium">{key.name}</p>
                    <p className="text-sm text-gray-600">{key.revokedAt ? "Revoked" : new Date(key.expiresAt) <= new Date() ? "Expired" : "Active"} · Expires {new Date(key.expiresAt).toLocaleDateString()} · Last used {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : "Never"}</p>
                    <p className="text-sm text-gray-600">{key.permissions.join(", ")}</p>
                </div>
                {!key.revokedAt && <button type="button" disabled={busy} className="text-red-700 underline self-start" onClick={() => revoke(key.id)}>Revoke</button>}
            </li>)}
        </ul>
        <form onSubmit={create} className="space-y-4">
            <label className="block">Key name<input required maxLength={200} value={name} onChange={event => setName(event.target.value)} placeholder="Power BI reporting" className="block border rounded p-2 w-full mt-1" /></label>
            <label className="block">Expires<input required type="date" value={expiry} onChange={event => setExpiry(event.target.value)} className="block border rounded p-2 mt-1" /></label>
            <fieldset><legend className="font-medium mb-2">Permissions</legend>
                {!permissions.length && <p className="text-sm text-gray-600">No permissions are available. Grant organization permissions to this {scope} before creating a key. API keys do not inherit the Admin role.</p>}
                <div className="max-h-64 overflow-y-auto space-y-2">{permissions.map(permission => <label key={permission.name} className="flex items-start gap-2">
                    <input type="checkbox" className="mt-1" checked={selected.includes(permission.name)} onChange={event => setSelected(previous => event.target.checked ? [...previous, permission.name] : previous.filter(name => name !== permission.name))} />
                    <span><span className="font-medium">{permission.displayName}</span><span className="block text-sm text-gray-600">{permission.description}</span></span>
                </label>)}</div>
            </fieldset>
            <button type="submit" disabled={busy || !organization || !subjectId || !selected.length} className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50">{busy ? "Saving…" : "Create API key"}</button>
        </form>
    </section>;
}
